import type {
  AstrolabeId,
  FoulKind,
  MatchFormat,
  RoundEndReason,
  TeamId,
  TurnEndReason,
} from "./match";
import { SHOT_LIMITS, isFiniteNumber, type ShotInput } from "./geometry";

/** 클라이언트 → 서버 */
export const ClientMessage = {
  /** 로비에서 준비 상태 토글 */
  Ready: "ready",
  /** 로비에서 팀 변경 요청 */
  ChangeTeam: "change_team",
  /** 슈팅 */
  Shoot: "shoot",
  /**
   * 조준 중 (내 차례에만). 서버는 검증 후 나머지 전원에게 AimUpdate로 중계한다.
   * 순전히 표시용 — 게임 상태를 바꾸지 않고, 놓쳐도(유실돼도) 문제없다.
   */
  Aim: "aim",
  /** Final Sequence 진입 시: 남은 공을 점수로 환산하고 턴 종료 */
  BankPoints: "bank_points",
  /** Final Sequence 진입 시: 남은 공 전부를 계속 사용 */
  ContinueFinalSequence: "continue_final_sequence",
  /** Grouping 된 자기 공 회수 (Returned Grouped Balls) */
  RetrieveGrouped: "retrieve_grouped",
  Chat: "chat",
} as const;

export type ClientMessageType = (typeof ClientMessage)[keyof typeof ClientMessage];

/** 서버 → 클라이언트 (Schema 델타로 안 가는 이벤트성 메시지들) */
export const ServerMessage = {
  /** 샷 시뮬레이션 결과 + 리플레이 프레임 */
  ShotResult: "shot_result",
  /** 다른 플레이어의 실시간 조준 (Aim 중계). 보낸 본인에게는 안 간다. */
  AimUpdate: "aim_update",
  /** 턴 주인 변경 */
  TurnChanged: "turn_changed",
  /** Final Sequence 선택 대기 */
  FinalSequenceOffer: "final_sequence_offer",
  RoundEnded: "round_ended",
  MatchEnded: "match_ended",
  /** 요청 거부 (검증 실패 등) */
  Rejected: "rejected",
  Chat: "chat",
} as const;

export type ServerMessageType = (typeof ServerMessage)[keyof typeof ServerMessage];

// ── 클라이언트 → 서버 페이로드 ─────────────────────────────

export interface JoinOptions {
  format: MatchFormat;
  name: string;
  /**
   * 팀당 공 개수. 12(기본) 또는 9999(무제한)만 허용된다.
   * 매치메이킹이 이 값으로도 나뉘므로 같은 설정끼리만 만난다.
   */
  ballsPerTeam?: number;
  /**
   * true면 비공개 방 — 자동 매칭 대상에서 빠지고, 방 코드(roomId)를 아는
   * 사람만 들어올 수 있다. 방 코드는 서버가 6자리로 발급한다.
   */
  privateRoom?: boolean;
  /**
   * 안착 공이 빠지면 점수 차감 (RulesConfig.knockoutPenalty). 기본 false.
   * 매치메이킹이 이 값으로도 나뉜다 — 다른 규칙끼리 만나면 안 된다.
   */
  knockoutPenalty?: boolean;
  /** Steamworks GetAuthSessionTicket() 결과 (hex). 개발 중에는 생략 가능. */
  steamTicket?: string;
}

/** JoinOptions.ballsPerTeam으로 고를 수 있는 값들. [기본, 무제한] */
export const BALLS_PER_TEAM_CHOICES = [12, 9999] as const;

export function sanitizeBallsPerTeam(value: unknown): number {
  return value === 9999 ? 9999 : 12;
}

export interface ChangeTeamPayload {
  team: TeamId;
}

export interface ChatPayload {
  text: string;
  /** true면 팀에게만 */
  teamOnly: boolean;
}

/**
 * 조준 중계 페이로드. 각도·세기만 보낸다 — 발사 지점은 자리 고정이라 이미
 * 공개 정보고, 스핀은 조준선으로 표현할 수 없어 굳이 흘리지 않는다.
 */
export interface AimPayload {
  /** 조준 각도 (radian). ShotInput.angle과 같은 좌표계. */
  angle: number;
  /** 세기 (m/s). ShotInput.power와 같은 범위. */
  power: number;
}

/** 슈팅과 같은 범위 검증 — 벗어나면 중계하지 않고 조용히 버린다 (표시용이라 거부 응답도 없다). */
export function validateAimPayload(input: unknown): input is AimPayload {
  if (typeof input !== "object" || input === null) return false;
  const a = input as Record<string, unknown>;
  if (!isFiniteNumber(a.angle) || !isFiniteNumber(a.power)) return false;
  if (Math.abs(a.angle) > SHOT_LIMITS.maxAngle) return false;
  if (a.power < SHOT_LIMITS.minPower || a.power > SHOT_LIMITS.maxPower) return false;
  return true;
}

// ── 서버 → 클라이언트 페이로드 ─────────────────────────────

/**
 * 샷 리플레이. 서버가 권위 시뮬레이션을 돌린 뒤 프레임을 그대로 내려준다.
 * 클라이언트는 물리를 재계산하지 않고 이걸 재생만 하므로 서버와 100% 일치한다.
 */
export interface ShotReplay {
  /** 시뮬레이션에 사용된 시드 (리플레이 재현용) */
  seed: number;
  /** 프레임 샘플링 주기 (Hz) */
  sampleHz: number;
  /**
   * 프레임 배열. 각 프레임은 [ballId, x, y] 삼중항이 평탄하게 이어진 배열.
   * 정지한 공은 생략되므로 프레임마다 길이가 다를 수 있다.
   */
  frames: number[][];
}

export interface ScoredBall {
  ballId: number;
  astrolabe: AstrolabeId;
  slot: number;
  /** 기본 점수 (1 / 2 / 5) */
  base: number;
  /** Chaining 보너스 */
  chainBonus: number;
  /** 이 공을 포함한 연쇄 길이 */
  chainLength: number;
  /** Grand Slam 보너스 */
  grandSlamBonus: number;
  total: number;
}

export interface ShotScore {
  team: TeamId;
  balls: ScoredBall[];
  /** 이 샷으로 얻은 총점 */
  total: number;
  /** 이 샷으로 완성된 Grand Slam 위치들 */
  grandSlams: AstrolabeId[];
}

export interface ShooterRef {
  sessionId: string;
  team: TeamId;
  /** 팀 내 슈팅 순서 인덱스 */
  slot: number;
}

/** 조준 중계 (서버 → 조준자를 제외한 전원). */
export interface AimBroadcast {
  sessionId: string;
  team: TeamId;
  angle: number;
  power: number;
}

/** 이 샷으로 칸에서 빠져나간(원래 안착해 있던) 공. */
export interface KnockedOutBall {
  ballId: number;
  /** 공의 소유 팀 — 차감도 이 팀이 받는다 */
  team: TeamId;
  /** 빠져나온 판 */
  astrolabe: AstrolabeId;
  /** 차감된 점수. knockoutPenalty가 꺼진 방이면 0 (빠지긴 했지만 점수 유지). */
  penalty: number;
}

export interface ShotResultPayload {
  /** 라운드 내 샷 순번 */
  shotId: number;
  by: ShooterRef;
  input: ShotInput;
  replay: ShotReplay;
  score: ShotScore;
  /** 샷 반영 후 팀별 누적 점수 [team0, team1] */
  totals: [number, number];
  /** 이 샷으로 칸에서 빠져나간 공들 (없으면 빈 배열) */
  knockouts: KnockedOutBall[];
  turnEnded: boolean;
  turnEndReason: TurnEndReason | null;
  foul: FoulKind | null;
}

export interface TurnChangedPayload {
  team: TeamId;
  /**
   * 이 턴이 속한 라운드.
   *
   * 자리(좌/우)는 라운드에서 유도되는데, Colyseus 상태 패치는 주기적으로 나가고
   * 메시지는 즉시 나가서 **라운드 전환 때 이 메시지가 상태보다 먼저 도착한다**.
   * 클라이언트가 state.round로 자리를 계산하면 이전 라운드 자리로 쏘게 되고
   * 서버가 play_line 파울로 거부한다. 그래서 라운드를 함께 실어 보낸다.
   */
  round: number;
  /** 이번에 쏠 플레이어. 전원 연결 끊김 등으로 없을 수 있다. */
  sessionId: string | null;
  /** 턴 마감 시각까지 남은 ms */
  turnTimeoutMs: number;
}

export interface FinalSequenceOfferPayload {
  team: TeamId;
  ballsRemaining: number;
  /** Bank 선택 시 받게 될 점수 */
  bankValue: number;
  /** 선택 제한시간 ms */
  decideWithinMs: number;
}

export interface RoundEndedPayload {
  round: number;
  /** 잔여 공 보너스 반영 후 팀별 점수 */
  totals: [number, number];
  /** 잔여 공 보너스 [team0, team1] */
  leftoverBonus: [number, number];
  nextPhase: "intermission" | "overtime" | "ended";
  reason: RoundEndReason;
  /** reason이 "grand_slam"일 때 어느 판을 누가 채웠는지 */
  grandSlam: { astrolabe: AstrolabeId; team: TeamId } | null;
}

export interface MatchEndedPayload {
  totals: [number, number];
  /** 무승부는 null (연장까지 갔는데도 동점인 경우) */
  winner: TeamId | null;
  reason: "score" | "overtime" | "forfeit";
}

export interface RejectedPayload {
  /** 거부된 클라이언트 메시지 타입 */
  message: ClientMessageType;
  code:
    | "not_your_turn"
    | "wrong_phase"
    | "invalid_payload"
    | "out_of_balls"
    | "play_line"
    | "not_allowed";
  detail?: string;
}

export interface ChatBroadcast {
  sessionId: string;
  name: string;
  team: TeamId;
  text: string;
  teamOnly: boolean;
}
